import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import PublicLayout from "@/components/PublicLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { UserPlus, Mail, CheckCircle2 } from "lucide-react";

export default function RegisterCitizen() {
  const { i18n } = useTranslation();
  const [interests, setInterests] = useState([]);
  const [form, setForm] = useState({ email: "", name: "", phone: "", language: i18n.language, interests: [], consent_communications: false });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => { api.get("/interests?active_only=true").then(({ data }) => setInterests(data)); }, []);

  const labelFor = (i) => i[`name_${i18n.language}`] || i.name_ca;

  const toggleInterest = (slug) => setForm(f => ({
    ...f,
    interests: f.interests.includes(slug) ? f.interests.filter(s => s !== slug) : [...f.interests, slug],
  }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent_communications) return toast.error("Cal acceptar el consentiment de comunicacions");
    if (form.interests.length === 0) return toast.error("Selecciona almenys un interès");
    setLoading(true);
    try {
      await api.post("/citizens/register", { ...form, origin_url: window.location.origin });
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally { setLoading(false); }
  };

  return (
    <PublicLayout>
      <div className="max-w-3xl mx-auto px-6 py-12">
        <Card className="rounded-3xl border-2 shadow-xl shadow-primary/5">
          <CardContent className="p-8 sm:p-12">
            {done ? (
              <div className="text-center py-12" data-testid="citizen-register-done">
                <div className="w-16 h-16 mx-auto rounded-2xl bg-emerald-100 text-emerald-600 flex items-center justify-center mb-4">
                  <CheckCircle2 className="w-8 h-8" aria-hidden="true" />
                </div>
                <h1 className="font-heading font-bold text-3xl">Comprova el teu correu!</h1>
                <p className="text-muted-foreground mt-3 max-w-md mx-auto">
                  T'hem enviat un enllaç de confirmació a <strong>{form.email}</strong>. Fes-hi clic per activar la teva subscripció.
                </p>
              </div>
            ) : (
              <>
                <div className="text-center mb-8">
                  <div className="w-14 h-14 mx-auto rounded-2xl bg-primary text-primary-foreground flex items-center justify-center mb-4">
                    <UserPlus className="w-6 h-6" aria-hidden="true" />
                  </div>
                  <div className="overline text-accent">Ciutadania</div>
                  <h1 className="font-heading font-bold text-3xl sm:text-4xl mt-2">Rep les novetats de Shopping Salou</h1>
                  <p className="text-muted-foreground mt-3 max-w-xl mx-auto">
                    Registra't gratuïtament i escull els teus interessos. T'enviarem només allò que t'interessa: promocions, activitats, fires, novetats dels comerços...
                  </p>
                </div>

                <form onSubmit={submit} className="space-y-6" data-testid="citizen-register-form">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div><Label>Nom *</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 rounded-xl" data-testid="citizen-name" /></div>
                    <div><Label>Correu electrònic *</Label><Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-2 rounded-xl" data-testid="citizen-email" /></div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div><Label>Telèfon (opcional)</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-2 rounded-xl" data-testid="citizen-phone" /></div>
                    <div><Label>Idioma preferit</Label>
                      <select value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value })} className="mt-2 w-full rounded-xl border-2 border-border h-10 px-3 bg-background" data-testid="citizen-language">
                        <option value="ca">Català</option><option value="es">Español</option><option value="en">English</option><option value="fr">Français</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <Label className="text-base font-heading">Els meus interessos *</Label>
                    <p className="text-xs text-muted-foreground mt-1">Escull tots els que et vinguin de gust</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-3">
                      {interests.map(i => (
                        <button
                          key={i.slug} type="button"
                          onClick={() => toggleInterest(i.slug)}
                          data-testid={`citizen-interest-${i.slug}`}
                          className={`text-left px-4 py-3 rounded-2xl border-2 transition-colors ${
                            form.interests.includes(i.slug)
                              ? "border-accent bg-accent/10 text-primary"
                              : "border-border hover:border-accent/50"
                          }`}
                        >
                          <div className="font-medium text-sm">{labelFor(i)}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-2xl border-2 border-border p-5 bg-secondary/40">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <Checkbox
                        checked={form.consent_communications}
                        onCheckedChange={(v) => setForm({ ...form, consent_communications: !!v })}
                        data-testid="citizen-consent"
                      />
                      <span className="text-sm">
                        <strong>Accepto</strong> rebre comunicacions de Shopping Salou sobre els meus interessos.
                        Podré donar-me de baixa en qualsevol moment des dels correus rebuts o gestionar les meves preferències.
                        Les meves dades es tracten segons la <a href="/politica-privacitat" className="text-primary hover:underline" target="_blank" rel="noreferrer">Política de Privacitat</a>.
                      </span>
                    </label>
                  </div>

                  <Button type="submit" size="lg" disabled={loading} className="w-full rounded-full h-12" data-testid="citizen-submit-btn">
                    <Mail className="w-4 h-4 mr-2" aria-hidden="true" /> {loading ? "Enviant..." : "Registrar-me"}
                  </Button>
                </form>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </PublicLayout>
  );
}
